import React from "react";


const SummaryRenderer = ({ summary }) => {
  if (!summary || typeof summary !== "object") {
    return <span className="text-gray-500">—</span>;
  }

  const entries = Object.entries(summary);

  const prettyTitle = (key) =>
    key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="space-y-4">
      {entries.map(([key, val]) => {
        if (!Array.isArray(val) || val.length === 0) return null;

        const isStringArray = typeof val[0] === "string";
        const isObjectArray = typeof val[0] === "object" && val[0] !== null;

        return (
          <div key={key} className="space-y-2">
            {/* H1-ish */}
            <div className="text-sm font-semibold text-gray-900">
              {prettyTitle(key)}
            </div>

            {/* Case A: array of strings -> bullet list */}
            {isStringArray && (
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-800">
                {val.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            )}

            {/* Case B: array of objects with name/notes -> name as H2 + notes bullets */}
            {isObjectArray && (
              <div className="space-y-3">
                {val.map((item, i) => {
                  const name = item?.name;
                  const notes = Array.isArray(item?.notes) ? item.notes : [];

                  // Fallback: if object doesn't match expected, render JSON string
                  if (!name && notes.length === 0) {
                    return (
                      <div
                        key={i}
                        className="text-xs text-gray-600 font-mono whitespace-pre-wrap"
                      >
                        {JSON.stringify(item, null, 2)}
                      </div>
                    );
                  }

                  return (
                    <div key={i} className="pl-2">
                      {/* H2-ish */}
                      {name && (
                        <div className="text-sm font-semibold text-gray-900">
                          {name}
                        </div>
                      )}

                      {notes.length > 0 && (
                        <ul className="mt-1 list-disc pl-5 space-y-1 text-sm text-gray-700">
                          {notes.map((n, j) => (
                            <li key={j}>{n}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


const interviewsData = [
  {
    id: "1",
    name: "Monsoon Pabrai",
    fund: "Drew Investment Management",
    fundUrl: "Drew Investment Management",
    linkLabel: "Exploring Minds",
    linkUrl: "https://www.youtube.com/watch?v=ipRzfQ_48ys", // <-- put the YouTube URL here
    summary: {
      "global_us_stocks": [
        {
          "name": "Alibaba Group",
          "notes": [
            "Mentioned as her biggest loss as a fund manager due to Chinese regulatory risk."
          ]
        },
        {
          "name": "TSMC (Taiwan Semiconductor Manufacturing Company)",
          "notes": [
            "Discussed in depth as a high-quality but geopolitically risky investment.",
            "She explicitly says she would cap position size due to China–Taiwan risk."
          ]
        },
        {
          "name": "Visa",
          "notes": [
            "First stock she noticed Warren Buffett buying.",
            "Described as a 'toll bridge' business model."
          ]
        },
        {
          "name": "Mastercard",
          "notes": [
            "Her first personal investment at age 16.",
            "Chosen because it was cheaper than Visa at the time."
          ]
        },
        {
          "name": "American Express",
          "notes": [
            "One of her highest-conviction global bets.",
            "Explained in detail why it was mispriced during COVID.",
            "Also noted as one of Warren Buffett’s biggest holdings."
          ]
        },
        {
          "name": "Costco Wholesale",
          "notes": [
            "Bought at approximately 40x earnings.",
            "Example of paying up for high-quality compounders."
          ]
        },
        {
          "name": "Amazon",
          "notes": [
            "Mentioned in the COVID spending context due to the online shopping surge."
          ]
        }
      ],
      "indian_stocks_financial_markets": [
        {
          "name": "National Stock Exchange of India (NSE)",
          "notes": [
            "Her highest-conviction bet in India.",
            "Compared to a 'toll bridge' and a 'cockroach that survives anything'.",
            "Strongly bullish long-term due to India’s market penetration."
          ]
        },
        {
          "name": "BSE Ltd",
          "notes": [
            "Compared directly with NSE on valuation.",
            "Expected to benefit from regulatory push for competition."
          ]
        },
        {
          "name": "HDFC Ltd",
          "notes": [
            "Part of her Indian financial services basket.",
            "Viewed as a clean, long-term India growth proxy."
          ]
        },
        {
          "name": "Motilal Oswal Financial Services",
          "notes": [
            "Chosen for brokerage account growth and market participation."
          ]
        },
        {
          "name": "Oberoi Realty",
          "notes": [
            "More than 50% profit margin, unique for a realty company"
          ]
        },
        {
          "name": "360 ONE WAM (formerly IIFL Wealth)",
          "notes": [
            "Selected as a UHNI wealth-management play."
          ]
        }
      ],
      "consumer_brand_examples": [
        {
          "name": "The Coca-Cola Company",
          "notes": [
            "Discussed via Coke Studio as a case study in cultural branding.",
            "Illustrative example, not an investment pick."
          ]
        },
        {
          "name": "PepsiCo",
          "notes": [
            "Mentioned in contrast with Coke’s marketing strategy in Pakistan.",
            "Illustrative example, not an investment pick."
          ]
        }
      ],
      "key_takeaways": [
        "Loves toll-bridge businesses (Visa, Mastercard, NSE, BSE).",
        "Strong preference for high-quality compounders.",
        "Willing to pay up for quality.",
        "Actively manages tail risk (TSMC, China exposure).",
        "Avoids businesses where regulatory or trust risk is high."
      ]
    },
  },
  {
    id: "2",
    name: "Aditya Khemka",
    fund: "InCred Asset Management",
    fundUrl: "InCred Asset Management",
    linkLabel: "Finance With Sharan",
    linkUrl: "https://www.youtube.com/watch?v=3sYmIUtvabk", // <-- put the YouTube URL here
    summary: {
      "indian_stocks_financial_markets": [
        {
          "name": "Torrent Pharmaceuticals",
          "notes": [
            "Cited as a strong long-term outperformer within Indian healthcare.",
            "Stock price increased from around 550 to over 3600 in roughly a decade.",
            "Majority of revenue comes from Indian branded generics, a high-growth market.",
            "Contrasted with export-heavy pharma companies that saw muted returns."
          ]
        },
        {
          "name": "Sun Pharmaceutical Industries",
          "notes": [
            "Referenced as a healthcare 'poster boy' that underperformed over the past decade.",
            "Share price growth was limited due to heavy exposure to slow-growing US and EU markets.",
            "Used as an example of why headline sector leaders can misrepresent true sector growth."
          ]
        },
        {
          "name": "Dr. Reddy’s Laboratories",
          "notes": [
            "Mentioned alongside other large export-focused pharma companies.",
            "Cited as delivering single-digit CAGR returns over a decade."
          ]
        },
        {
          "name": "Cipla",
          "notes": [
            "Grouped with other large pharma exporters with modest long-term returns.",
            "Used to explain investor misperception of the healthcare sector."
          ]
        },
        {
          "name": "Healthcare Global Enterprises",
          "notes": [
            "Identified as a core holding and a top conviction investment.",
            "Oncology-focused hospital chain operating across multiple regions in India.",
            "Benefits from higher revenue per bed due to specialized cancer treatment.",
            "Representative of the fast-growing hospital sub-sector."
          ]
        },
        {
          "name": "Krishna Diagnostics",
          "notes": [
            "Referenced as an example of a public-private partnership (B2G) diagnostics model.",
            "Benefits from large-scale testing volumes despite lower per-test pricing."
          ]
        },
        {
          "name": "Thyrocare Technologies",
          "notes": [
            "Mentioned as an example of a B2B backend diagnostics model.",
            "Scale-driven cost advantages allow competitive pricing and healthy margins."
          ]
        },
        {
          "name": "Dr. Lal PathLabs",
          "notes": [
            "Cited as a B2C branded diagnostics player.",
            "Higher valuation compared to peers but slower growth relative to volume-led models."
          ]
        }
      ],

      "global_us_stocks": [],

      "consumer_brand_examples": [],

      "key_takeaways": [
        "Indian healthcare has significantly outperformed broader indices over the last decade.",
        "Export-focused pharma companies operate in low-growth markets, masking sector potential.",
        "Hospitals, diagnostics, and CDMO are the fastest-growing healthcare sub-sectors.",
        "Diagnostics is highly under-organized, with quality compliance still extremely low.",
        "Geopolitical shifts are accelerating API and CDMO capacity expansion in India.",
        "Healthcare demand is recession-proof due to its non-discretionary nature.",
        "Long-term wealth allocation favors secular growth sectors over cyclical rotation."
      ]
    }
  },
  {
    id: "3",
    name: "Amit Jeswani",
    fund: "Stallion PMS",
    fundUrl: "Stallion PMS",
    linkLabel: "Sonia Shenoy",
    linkUrl: "https://www.youtube.com/watch?v=kNqisU3Kwg0&t=1167s", // <-- put the YouTube URL here
    summary: {
      "global_us_stocks": [
        {
          "name": "Amazon",
          "notes": [
            "Referenced as a benchmark for scale in consumer and e-commerce businesses.",
            "Used to compare the size of Indian retail opportunity versus global leaders."
          ]
        }
      ],

      "indian_stocks_financial_markets": [
        {
          "name": "Trent Ltd",
          "notes": [
            "One of the fund’s major winners over multiple years.",
            "Chosen due to strong execution and rapid revenue scaling.",
            "Position size reduced when growth decelerated from very high levels.",
            "Used as an example of riding winners and trimming when execution slows."
          ]
        },
        {
          "name": "Zomato",
          "notes": [
            "Initially bought at listing and later exited when losses increased.",
            "Re-entered after the company turned profitable, following a defined framework.",
            "Growth in Blinkit and quick-commerce cited as key drivers.",
            "Compared favorably against competitors based on market-share gains."
          ]
        },
        {
          "name": "Polycab India",
          "notes": [
            "Identified early as a market leader with strong execution.",
            "Held through large scale-up phase as revenues and margins expanded.",
            "Example of a monopoly-like leader within its industry."
          ]
        },
        {
          "name": "DMart (Avenue Supermarts)",
          "notes": [
            "Held during high-growth phase when earnings growth exceeded 20%.",
            "Exited when growth slowed below the internal execution threshold.",
            "Capital rotated into faster-scaling opportunities."
          ]
        },
        {
          "name": "Varun Beverages",
          "notes": [
            "Cited as a standout consumer growth company.",
            "Demonstrated strong volume growth and reinvestment at high ROCE.",
            "Outperformed larger FMCG peers growing at low single digits."
          ]
        },
        {
          "name": "Policybazaar",
          "notes": [
            "Exited despite later price appreciation.",
            "Used as an example of disciplined selling based on frameworks, not emotions."
          ]
        },
        {
          "name": "Nykaa",
          "notes": [
            "Cited as the single-largest loss in the portfolio historically.",
            "Loss was tightly controlled at the portfolio level (~1–2%).",
            "Used to highlight the importance of cutting losers early."
          ]
        },
        {
          "name": "International Gemological Institute (IGI)",
          "notes": [
            "Identified as a dominant global player in lab-grown diamond certification.",
            "Controls a majority share of global certification volume.",
            "Viewed as a high-growth, high-margin monopoly-like business.",
            "Investment based on rapid adoption of lab-grown diamonds."
          ]
        }
      ],

      "consumer_brand_examples": [
        {
          "name": "Red Bull",
          "notes": [
            "Used as an example of a premium-priced market leader.",
            "Contrasted with lower-priced disruptors entering mass markets."
          ]
        }
      ],

      "key_takeaways": [
        "Risk management is more important than absolute returns.",
        "It is not what you make in markets, but what you retain over time.",
        "Focus on top executors and market leaders, not average companies.",
        "Riding winners aggressively and cutting losers early is essential.",
        "Be reactive to change.",
        "Execution and market-share gains matter more than narratives.",
        "Growth investing works best when combined with strict risk controls.",
        "Capital allocation is the most critical decision in investing.",
        "There is no loyalty to promoters; only performance matters.",
        "Continuous learning and framework-based decision-making are key.",
        "Long-term wealth is built by compounding at high rates with low drawdowns."
      ]
    }
  },

  {
    id: "4",
    name: "Samir Arora",
    fund: "Helios Capital",
    fundUrl: "Helios Capital",
    linkLabel: "Thrive by Groww",
    linkUrl: "https://www.youtube.com/watch?v=qj2avX1OVUg", // <-- put the YouTube URL here
    summary: {
    "global_us_stocks": [
      {
        "name": "Amazon",
        "notes": [
          "Used to highlight investor perception versus reality — even large-cap leaders can deliver negative one-year returns.",
          "Cited after a sharp 16% fall to illustrate volatility in mega caps."
        ]
      },
      {
        "name": "Meta Platforms",
        "notes": [
          "Referenced as part of US tech stocks delivering negative returns over a one-year period.",
          "Used to challenge investor overconfidence in global tech leadership."
        ]
      },
      {
        "name": "Microsoft",
        "notes": [
          "Mentioned alongside Amazon and Meta as examples of temporary underperformance in global leaders."
        ]
      },
      {
        "name": "Nvidia",
        "notes": [
          "Referenced in context of US tech outperformance phase post-2017.",
          "Example of concentrated leadership within US markets."
        ]
      },
      {
        "name": "Netflix",
        "notes": [
          "Cited as part of US tech rally during earlier global allocation phase."
        ]
      },
      {
        "name": "Alibaba",
        "notes": [
          "Used in context of China allocation challenges.",
          "Example of complexity in global investing without local research depth."
        ]
      }
    ],

    "indian_stocks_financial_markets": [
      {
        "name": "Reliance Industries",
        "notes": [
          "Viewed through unlocking theme of telecom and retail businesses.",
          "Portfolio allocation adjusted depending on style balance and liquidity considerations."
        ]
      },
      {
        "name": "Zomato (Eternal)",
        "notes": [
          "Maintained conviction despite founder moving to non-executive role.",
          "Management continuity believed to remain intact.",
          "Position sized meaningfully in portfolio."
        ]
      },
      {
        "name": "Ather Energy",
        "notes": [
          "Invested as EV disruptor in two-wheeler space.",
          "Positioned as beneficiary of automotive transition."
        ]
      },
      {
        "name": "KPIT Technologies",
        "notes": [
          "Seen as enabler of automotive EV transition via software and engineering services.",
          "Beneficiary of disruption rather than victim."
        ]
      },
      {
        "name": "Bharti Airtel",
        "notes": [
          "Referenced historically as eventual telecom winner after industry shakeout.",
          "Used to illustrate that losers are easier to identify than winners."
        ]
      }
    ],

    "consumer_brand_examples": [
      {
        "name": "Apple",
        "notes": [
          "Discussed in context of over-dependence on China manufacturing.",
          "Illustrates strategic risk of geographic concentration."
        ]
      },
      {
        "name": "Volvo",
        "notes": [
          "Referenced to demonstrate capital destruction risk in global auto sector during EV transition."
        ]
      },
      {
        "name": "Louis Vuitton",
        "notes": [
          "Used to explain currency risk complexity in global investing.",
          "Example of euro versus dollar performance differences."
        ]
      }
    ],

    "macro_themes_discussed": [
      "India underperformance versus emerging markets.",
      "US-India trade deal and tariff normalization.",
      "China-plus-one structural shift.",
      "Importance of foreign institutional flows.",
      "AI disruption risk to Indian IT services.",
      "Automotive EV transition increasing competition.",
      "High-frequency trading cost versus liquidity trade-off.",
      "Gold as hedge against currency and geopolitical risk.",
      "Silver viewed as speculative and squeeze-driven.",
      "ETF approach for global allocation.",
      "Earnings growth as primary long-term return driver."
    ],

    "key_takeaways": [
      "It is easier to identify potential losers than to predict winners.",
      "Portfolio outperformance comes from avoiding bottom-quartile stocks.",
      "Earnings growth ultimately drives long-term market returns.",
      "Avoid anchoring to past market leaders.",
      "Disruption increases competition and capital intensity.",
      "AI introduces structural uncertainty for labor-intensive sectors.",
      "Global investing requires deep infrastructure; otherwise prefer ETFs.",
      "Gold functions as hedge during geopolitical and currency instability.",
      "Market returns from current levels likely align with earnings growth (~13–15%).",
      "Nobody knows anything with certainty beyond a point."
    ]
    }
  },

  {
    id: "5",
    name: "N JayaKumar",
    fund: "Prime Securities",
    fundUrl: "Prime Securities",
    linkLabel: "The BroadView",
    linkUrl: "https://www.youtube.com/watch?v=iCmW4XswPZM", // <-- put the YouTube URL here
    summary: {
      "global_us_stocks": [
        {
          "name": "Google",
          "notes": [
            "Used as an example of success through timing, patience, and execution versus earlier players like Yahoo.",
            "Illustrates importance of being right at the right time, not just early."
          ]
        },
        {
          "name": "Yahoo",
          "notes": [
            "Referenced as an early mover that failed to sustain leadership.",
            "Used to highlight that first-mover advantage does not guarantee long-term success."
          ]
        },
        {
          "name": "Meta Platforms",
          "notes": [
            "Referenced in context of data center demand and large-scale AI infrastructure requirements.",
            "Example of hyperscaler driving global data infrastructure growth."
          ]
        }
      ],

      "indian_stocks_financial_markets": [
        {
          "name": "HDFC Bank",
          "notes": [
            "Example of a boring compounding business that delivered massive long-term returns.",
            "Also cited as currently underperforming due to merger-related issues and growth constraints."
          ]
        },
        {
          "name": "Bharti Airtel",
          "notes": [
            "Referenced as a long-term telecom winner.",
            "Example of selling too early despite strong long-term compounding potential."
          ]
        },
        {
          "name": "JSW Steel",
          "notes": [
            "Identified early in metals cycle but exited too early.",
            "Illustrates importance of staying invested in cyclical winners."
          ]
        },
        {
          "name": "Jindal Steel",
          "notes": [
            "Another metals play exited prematurely.",
            "Shows challenge of holding through full commodity cycles."
          ]
        },
        {
          "name": "Aurobindo Pharma",
          "notes": [
            "Highlighted as attractive pharma player with export-driven growth.",
            "Strong positioning in generics and global markets."
          ]
        },
        {
          "name": "Lupin",
          "notes": [
            "Mentioned as a preferred pharma company with improving fundamentals.",
            "Beneficiary of global generics demand and margin stability."
          ]
        },
        {
          "name": "Sun Pharma",
          "notes": [
            "Market leader in Indian pharma.",
            "Well valued but still potential for long-term scale expansion."
          ]
        },
        {
          "name": "Federal Bank",
          "notes": [
            "Part of mid-tier banking ecosystem benefiting from capital inflows."
          ]
        },
        {
          "name": "RBL Bank",
          "notes": [
            "Example of smaller bank participating in growth cycle."
          ]
        },
        {
          "name": "IDBI Bank",
          "notes": [
            "Referenced in context of structural changes and capital infusion in banking sector."
          ]
        }
      ],

      "sector_views": {
        "pharma": {
          "view": "Strong Positive (Double Green)",
          "rationale": [
            "Low-cost global producer advantage for India.",
            "Export-driven growth with weak INR tailwind.",
            "Stable pricing environment in global markets.",
            "Balance sheets repaired; many companies debt-free.",
            "Index weight low (~3.3%) → scope for re-rating.",
            "Shift toward high-margin segments like injectables, CDMO, CRAMS."
          ]
        },
        "banking": {
          "view": "Mixed",
          "small_banks": "Positive (Green)",
          "large_banks": "Neutral (Yellow)",
          "rationale": [
            "Large banks constrained by size and steady growth expectations.",
            "Small banks can grow via NBFC acquisitions and capital infusion.",
            "India entering strong credit cycle with large capital inflows."
          ]
        },
        "IT_services": {
          "view": "Negative / Uncertain (Red)",
          "rationale": [
            "AI disruption creating uncertainty in business models.",
            "Currency benefits not translating into margin expansion.",
            "Multiples already compressed."
          ]
        },
        "defense": {
          "view": "Mixed",
          "rationale": [
            "Large players priced to perfection.",
            "Order-driven and episodic revenue model.",
            "Smaller niche players may offer better opportunities."
          ]
        },
        "data_centers_power": {
          "view": "Positive (Green)",
          "rationale": [
            "AI-driven data demand explosion.",
            "Indirect plays via power and infrastructure.",
            "Large global contracts (Meta, Google scale)."
          ]
        }
      },

      "macro_themes_discussed": [
        "Extreme fear phase in markets indicating opportunity.",
        "Foreign institutional outflows impacting sentiment.",
        "India entering strong credit cycle.",
        "Weak rupee benefiting export-oriented sectors.",
        "AI disruption across industries.",
        "Shift from cheap labor model to AI-driven economy.",
        "Importance of liquidity cycles (March effect).",
        "Anti-consensus investing as core strategy."
      ],

      "key_takeaways": [
        "Best opportunities emerge during extreme fear phases.",
        "Avoid consensus trades — overcrowding leads to poor returns.",
        "If it's obvious, it's probably wrong.",
        "Lonely investing path is often necessary for outsized returns.",
        "Focus on longevity and solvency of businesses.",
        "Disruption (especially AI) is a major risk factor.",
        "Small banks may outperform due to structural tailwinds.",
        "Pharma offers one of the strongest sectoral opportunities.",
        "Boring compounding businesses create the highest wealth.",
        "Patience and staying invested are underrated virtues."
      ],

      "investing_framework": {
        "green_flags": [
          "Business longevity and solvency.",
          "Global scalability.",
          "Strong cash flows and moat.",
          "Ability to adapt to technology disruption.",
          "Low investor crowding."
        ],
        "red_flags": [
          "High consensus trades.",
          "Government payment dependency risk.",
          "Uncertain impact from AI disruption.",
          "Overcrowded sectors with no differentiation."
        ],
        "philosophy": [
          "Investing is a craft, not pure science or art.",
          "Be uncomfortable — comfort indicates consensus.",
          "Necessary condition for success is going against the crowd.",
          "Luck + patience + discipline drive outcomes."
        ]
      }
    }
  }

];

const FieldRow = ({ label, value }) => (
  <div className="flex items-start gap-2">
    <div className="w-16 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">
      {label}
    </div>
    <div className="text-sm text-gray-900 break-words">{value}</div>
  </div>
);

export default function Interviews() {

const [openId, setOpenId] = React.useState(null);
const toggleRow = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="mx-auto max-w-6xl px-4 py-5">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Interviews
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Quick scan of investing interviews with structured highlights.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl px-4 py-6">
        {/* ✅ Mobile cards */}
        <div className="md:hidden space-y-3">
          {interviewsData.map((itv) => {
            const isOpen = openId === itv.id;

            return (
              <div
                key={itv.id}
                className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleRow(itv.id)}
                  className="w-full px-4 py-4 text-left flex items-start gap-3 active:bg-gray-50"
                  aria-expanded={isOpen}
                >
                  <span
                    className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-600"
                    title={isOpen ? "Collapse" : "Expand"}
                  >
                    {isOpen ? "▾" : "▸"}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900 break-words">
                      {itv.name}
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-600">
                        <span className="text-gray-400">Fund:</span> {itv.fund}
                      </span>

                      {itv.linkUrl ? (
                        <a
                          href={itv.linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                          title={itv.linkLabel || "Open source"}
                          onClick={(e) => e.stopPropagation()} // ✅ don’t toggle accordion when tapping link
                        >
                          {itv.linkLabel || "Source"}
                        </a>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                          {itv.linkLabel || "Source"}
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4">
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <SummaryRenderer summary={itv.summary} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ✅ Desktop table */}
        <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Interview
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Fund
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Source
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {interviewsData.map((itv) => {
                  const isOpen = openId === itv.id;

                  return (
                    <React.Fragment key={itv.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => toggleRow(itv.id)}
                            className="flex items-center gap-3 text-left"
                            aria-expanded={isOpen}
                          >
                            <span
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-gray-600"
                              title={isOpen ? "Collapse" : "Expand"}
                            >
                              {isOpen ? "▾" : "▸"}
                            </span>

                            <span className="text-sm font-semibold text-gray-900">
                              {itv.name}
                            </span>
                          </button>
                        </td>

                        <td className="px-5 py-4">
                          <span className="text-sm text-gray-800">{itv.fund}</span>
                        </td>

                        <td className="px-5 py-4">
                          {itv.linkUrl ? (
                            <a
                              href={itv.linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                              title={itv.linkLabel || "Open source"}
                            >
                              {itv.linkLabel || "Source"}
                            </a>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                              {itv.linkLabel || "Source"}
                            </span>
                          )}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-white">
                          <td colSpan={3} className="px-5 pb-5">
                            <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                              <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 lg:col-span-8">
                                  <SummaryRenderer summary={itv.summary} />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}
